/**
 * Adult ASD Semi-Structured Interview — Main Page
 *
 * Entry-point for the Adult Autism Assessment based on DSM-5 criteria.
 * Guides clinicians through each criterion (A1-A3, B1-B4) using structured
 * probing questions and notes. Functional impairment is captured within the
 * criteria pages themselves (via the per-criterion "Domains Impacted" chips),
 * so there is no separate functional-impairment panel.
 *
 * Architecture mirrors the CYP ASD SSI (sidebar + main panel wizard) but
 * adapted for adult-specific content:
 *   - Left sidebar  — shared ModularSideNavbar in compact mode (criterion
 *                      badges with childhood/adulthood completion tracking)
 *   - Right panel   — AdultAsdCriterionPanel (when a criterion is selected)
 *   - Data is read from and written to Firestore via ModularPatientDataContext
 *     at assessmentStages.semiStructuredInterview.data.criteria.{criterionId}
 *   - A fixed floating footer lets clinicians toggle visual preferences.
 *
 * Session storage is used as a stale-snapshot guard: in-progress edits are
 * cached to sessionStorage so that a context refetch doesn't resurrect deleted
 * notes before the write propagates.
 */

import React, { useState, useCallback, useMemo, useEffect, useContext, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { usePreviewAwareNavigate } from '../../../hooks/usePreviewAwareNavigate';
import { ExclamationIcon } from '@heroicons/react/solid';
import { useVideoStream } from '../../../context/VideoStreamContext';
import { ModularPatientDataContext } from '../../../context/ModularPatientDataContext';
import { useIsPreviewMode } from '../../../hooks/useIsPreviewMode';
import { useCanEditStage } from '../../../hooks/useCanEditStage';
import { useAuth } from '../../../hooks/useAuth';
import useWorkflowConfig from '../../../hooks/useWorkflowConfig';
import { TriggerGenerateDocumentSection } from '../../../utils/TriggerGenerateDocumentSection';
import { useModularSectionExitRegeneration } from '../../../hooks/useModularSectionExitRegeneration';
import Layout from '../../../components/Layout';
import Breadcrumb from '../../../components/Breadcrumb';
import AppointmentChecklistControls from '../../../components/autismModular/AppointmentChecklistControls';
import { DocumentReportIcon } from '@heroicons/react/outline';
import ModularSideNavbar from '../../../components/autismModular/ModularSideNavbar';
import AdultAsdCriterionPanel from './components/AdultAsdCriterionPanel';
import SSI_CRITERIA, { hasAsdSsiCriteriaInput } from './config/adultAsdSSIConfig';
import { moveNoteBetweenArrays } from '../../../utils/structuredNotesMove';
import { computeSSIProgress } from '../../../utils/autismSSIProgress';
import { buildFormDataEntryFromInstance } from '../5_patientOverview/utils/buildModularPatientOverviewModel';

export default function AdultAsdSSI() {
  const { patientId } = useParams();
  const navigate = usePreviewAwareNavigate();
  const location = useLocation();
  const videoStream = useVideoStream();

  // ── Context: Firestore-backed patient data ─────────────────────────────────
  // assessmentStages holds all stage docs; header provides clinician assignment
  // info for permission gating. updateAssessmentStage performs debounced
  // Firestore writes; flushStageWrites awaits all in-flight writes before nav.
  const {
    header,
    formInstances,
    assessmentStages,
    reportContent,
    updateAssessmentStage,
    flushStageWrites,
  } = useContext(ModularPatientDataContext);

  // ── Permission gating ──────────────────────────────────────────────────────
  // Read-only unless the current user is the assigned SSI clinician AND the
  // page was not opened in preview mode (?preview=true from overview card).
  const permissionModel = header || {};
  const isPreviewMode = useIsPreviewMode();
  const canEdit = useCanEditStage(permissionModel, 'SSI', { previewMode: isPreviewMode });
  const { claims } = useAuth();
  const wf = useWorkflowConfig(header, claims);

  // Track whether we have already transitioned status from notStarted -> inProgress
  // so we only include the status update on the very first write in this session.
  const hasStartedRef = useRef(false);

  useEffect(() => {
    videoStream?.setVideoEnabled(true);
    return () => videoStream?.setVideoEnabled(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Active criterion selection ─────────────────────────────────────────────
  // Restores from location.state.activeItemId (when returning from Review) or
  // defaults to the first available criterion.
  const [activeItemId, setActiveItemId] = useState(() => {
    const fromReview = location.state?.activeItemId;
    if (fromReview && SSI_CRITERIA.some(c => c.id === fromReview)) return fromReview;
    return SSI_CRITERIA.find(c => c.available)?.id || SSI_CRITERIA[0].id;
  });

  // ── Session storage cache key ──────────────────────────────────────────────
  // Prevents stale Firestore snapshots from resurrecting deleted notes between
  // context refetches (same pattern as AdultAutismHistory panelNotes).
  const sessionKey = `adult-asd-ssi-session-${patientId}`;

  // ── All form data for all criteria ─────────────────────────────────────────
  // Initialised from sessionStorage (takes priority as in-session truth) then
  // falls back to context (Firestore). Keyed by criterion ID (A1-B4).
  const [allData, setAllData] = useState(() => {
    // Prefer session cache if present (guards against stale context on remount)
    try {
      const cached = sessionStorage.getItem(sessionKey);
      if (cached) return JSON.parse(cached);
    } catch { /* ignore */ }
    return assessmentStages?.semiStructuredInterview?.data?.criteria || {};
  });

  // Seed from context when it arrives (first load or context refresh) — only
  // backfill keys that are NOT already in the local session state. This prevents
  // Firestore from overwriting in-flight edits or resurrecting deleted notes.
  useEffect(() => {
    const contextCriteria = assessmentStages?.semiStructuredInterview?.data?.criteria;
    if (!contextCriteria || typeof contextCriteria !== 'object') return;

    setAllData(prev => {
      const merged = { ...prev };
      let changed = false;
      for (const [key, val] of Object.entries(contextCriteria)) {
        // Only seed keys we have never touched in this session
        if (merged[key] === undefined) {
          merged[key] = val;
          changed = true;
        }
      }
      return changed ? merged : prev;
    });
  }, [assessmentStages?.semiStructuredInterview?.data?.criteria]);

  // Persist allData to sessionStorage on every change (stale-snapshot guard)
  useEffect(() => {
    try {
      sessionStorage.setItem(sessionKey, JSON.stringify(allData));
    } catch { /* quota exceeded — acceptable */ }
  }, [sessionKey, allData]);

  // ── Stage progress persistence ────────────────────────────────────────────
  // Persist a canonical progress fraction (0-1) to the stage doc so the
  // backend can read it for dashboard pills. Mirrors OBS computeAndWriteProgress.
  // computeSSIProgress returns 0-100 so we divide by 100 for unit consistency.
  // Guard: once completed (status 'completed'), never write a smaller value.
  // Ref-based dedup prevents redundant writes when the computed value is stable
  // (defensive — SSI deps are local state / primitives, so no deep-clone loop).
  const lastPersistedSSIProgressRef = useRef(null);
  useEffect(() => {
    if (!canEdit) return;
    const ssiStage = assessmentStages?.semiStructuredInterview;
    const ssiData = { ...ssiStage, data: { ...ssiStage?.data, criteria: allData } };
    const progressPct = computeSSIProgress(ssiData);
    const progress = progressPct / 100;
    if (ssiStage?.status === 'completed' && progress < 1) return;
    if (lastPersistedSSIProgressRef.current === progress) return;
    lastPersistedSSIProgressRef.current = progress;
    updateAssessmentStage('semiStructuredInterview', { progress });
  }, [canEdit, allData, assessmentStages?.semiStructuredInterview?.status, updateAssessmentStage]);

  // Fixed display settings — no longer user-configurable (production mode).
  const displayPreferences = {
    evidenceMode: 'buttons',
    sidebarMode: 'compact',
    navMode: 'bottom',
  };

  // ── SEQ (Sensory Experiences Questionnaire) form data for the B4 panel ─────
  // When the patient's org has assigned the SEQ, the B4 criterion panel shows a
  // "View Sensory Experiences Questionnaire" button that opens FormDetailsPanel with the patient's
  // answers. The form instance must pass through buildFormDataEntryFromInstance
  // to normalise selectMultiple answers into the shape FormDetailsPanel expects.
  const seqFormData = useMemo(() => {
    const seqInstance = formInstances?.sensoryExperiences;
    if (!seqInstance) return null;
    return buildFormDataEntryFromInstance(seqInstance);
  }, [formInstances]);

  // ── Validation dialog state for incomplete evidence decisions ───────────────
  const [showValidationDialog, setShowValidationDialog] = useState(false);

  /**
   * Identifies criteria still missing evidence decisions. Each entry has the
   * criterion id, shortTitle, and which stages (childhood/adulthood) are missing.
   */
  const incompleteSymptoms = useMemo(() => {
    return SSI_CRITERIA.filter(c => c.available).reduce((acc, c) => {
      const d = allData[c.id] || {};
      const missingChildhood = !d.evidence_childhood;
      const missingAdulthood = !d.evidence_adulthood;
      if (missingChildhood || missingAdulthood) {
        acc.push({
          id: c.id,
          shortTitle: c.shortTitle,
          missingChildhood,
          missingAdulthood,
        });
      }
      return acc;
    }, []);
  }, [allData]);

  // ── Firestore status tracking ──────────────────────────────────────────────
  // On the first write when the stage status is 'notStarted', we also promote
  // the status to 'inProgress'. This ref ensures we only check/write once.
  const maybePromoteStatus = useCallback((dotPathUpdates) => {
    const currentStatus = assessmentStages?.semiStructuredInterview?.status;
    if (!hasStartedRef.current && currentStatus === 'notStarted') {
      hasStartedRef.current = true;
      return { ...dotPathUpdates, status: 'inProgress' };
    }
    return dotPathUpdates;
  }, [assessmentStages?.semiStructuredInterview?.status]);

  // ── LLM generation triggers ──────────────────────────────────────────
  // Track edits within the current criterion panel so we know whether to
  // trigger paragraph regeneration on navigation away.
  const [hasChanges, setHasChanges] = useState(false);

  // The Adult ASD SSI produces a single "Criteria" report section: every
  // criterion paragraph (A1-B4) is grouped under it. Functional Impairment has
  // been folded into the criteria pages (its standalone panel is intentionally
  // hidden), so there is no separate FI report section to generate.
  const currentReportSection = 'Criteria';

  // Has the clinician recorded any evidence, notes, or domain selections in
  // the local optimistic allData? When false, generation must not fire because
  // the backend extractor (extract_criteria_inputs) would return an empty dict
  // and main.py would abort with a 500. Checking the local copy rather than
  // the Firestore snapshot ensures the guard is accurate even before the
  // debounced write lands — the same pattern as canGenerateForSection on the
  // CYP CI page.
  const hasAnyCriteriaInput = useMemo(
    () => hasAsdSsiCriteriaInput(allData),
    [allData],
  );

  // Wire the unmanaged-exit safety net. On breadcrumb / browser back, the
  // hook fires generation for the section the clinician was last viewing.
  const { markRegenHandled, resetRegenHandled } = useModularSectionExitRegeneration({
    patientId,
    stageKey: 'semiStructuredInterview',
    sectionTitle: currentReportSection,
    hasChanges,
    canEdit,
    reportContent,
    // Block generation when no criteria have recorded evidence, notes, or
    // domains. Without this guard, an empty first visit (or Proceed Anyway
    // with no evidence) would fire a Cloud Function call that 500s.
    shouldRegenerate: hasAnyCriteriaInput,
  });

  /**
   * Triggers generation for the SSI report section being left, if there
   * have been edits or the section has not been generated before. Managed
   * navigation handlers call markRegenHandled() first so the unmount
   * cleanup skips a duplicate trigger.
   */
  const triggerSSIGeneration = useCallback((reportSection) => {
    if (!canEdit) return;
    // Guard: do not fire generation when the clinician has not recorded any
    // evidence, notes, or domain selections. The backend extractor would
    // return an empty dict and main.py would abort with a 500.
    if (!hasAnyCriteriaInput) return;

    const stageReport = reportContent?.semiStructuredInterview;
    const generated = stageReport?.stageSectionsGenerated || [];
    const isGenerated = generated.includes(reportSection);

    if (hasChanges || !isGenerated) {
      const dataReady = flushStageWrites('semiStructuredInterview');
      TriggerGenerateDocumentSection(
        patientId, 'Autism Semi-Structured Interview', reportSection,
        [], dataReady,
      );
    }
  }, [canEdit, patientId, reportContent, hasChanges, flushStageWrites, hasAnyCriteriaInput]);

  // ── Field change handler (optimistic local + Firestore write) ──────────────
  // Updates a single field within the currently active criterion/FI section.
  // Local state is updated immediately for responsiveness; the Firestore write
  // is debounced by the context layer.
  const handleFieldChange = useCallback((fieldId, value) => {
    setAllData(prev => {
      const updated = {
        ...prev,
        [activeItemId]: { ...(prev[activeItemId] || {}), [fieldId]: value },
      };
      return updated;
    });
    if (!canEdit) return;
    setHasChanges(true);
    const dotPath = { [`data.criteria.${activeItemId}.${fieldId}`]: value };
    updateAssessmentStage('semiStructuredInterview', maybePromoteStatus(dotPath));
  }, [activeItemId, canEdit, updateAssessmentStage, maybePromoteStatus]);

  // ── Atomic note move (global "criterionId:fieldKey" addressing) ────────────
  // Moves a note between two notes boxes identified by global ids of the form
  // "<criterionId>:<fieldKey>" (e.g. "A1:notes_childhood"). This single handler
  // serves BOTH interactions:
  //   - the same-criterion drag (Childhood <-> Adulthood), where the criterion
  //     panel translates its local field keys into global ids, and
  //   - the cross-criterion "Move" picker, where source and target belong to
  //     different criteria.
  // Every criterion lives in the one SSI stage document, so the source and
  // target updates persist in a single atomic write (no two-write race). allData
  // is fully seeded from context, so reading arrays directly from it is safe.
  const handleMoveNoteAcross = useCallback((sourceId, noteId, targetId, insertAt, notePatch) => {
    if (sourceId === targetId) return;
    const [srcCrit, srcField] = String(sourceId).split(':');
    const [tgtCrit, tgtField] = String(targetId).split(':');
    if (!srcCrit || !srcField || !tgtCrit || !tgtField) return;

    const sourceNotes = allData[srcCrit]?.[srcField] || [];
    const targetNotes = allData[tgtCrit]?.[tgtField] || [];
    const result = moveNoteBetweenArrays(sourceNotes, targetNotes, noteId, insertAt, notePatch);
    if (!result) return;

    // Optimistic local update. When both ends are in the same criterion we must
    // merge both fields into one object so the second does not clobber the first.
    setAllData(prev => {
      if (srcCrit === tgtCrit) {
        return {
          ...prev,
          [srcCrit]: { ...prev[srcCrit], [srcField]: result.source, [tgtField]: result.target },
        };
      }
      return {
        ...prev,
        [srcCrit]: { ...prev[srcCrit], [srcField]: result.source },
        [tgtCrit]: { ...prev[tgtCrit], [tgtField]: result.target },
      };
    });

    if (!canEdit) return;
    setHasChanges(true);
    updateAssessmentStage('semiStructuredInterview', maybePromoteStatus({
      [`data.criteria.${srcCrit}.${srcField}`]: result.source,
      [`data.criteria.${tgtCrit}.${tgtField}`]: result.target,
    }));
  }, [allData, canEdit, updateAssessmentStage, maybePromoteStatus]);

  // Catalogue of every notes box across all available criteria, used by the
  // per-note "Move" picker. Ids match the global addressing used by
  // handleMoveNoteAcross. All criteria share the single "Criteria" report
  // section, so cross-criterion moves need no special regeneration handling.
  const noteMoveTargets = useMemo(() => {
    const targets = [];
    for (const c of SSI_CRITERIA) {
      if (!c.available) continue;
      const group = `${c.id}. ${c.shortTitle}`;
      targets.push({ id: `${c.id}:notes_childhood`, label: 'Childhood', group });
      targets.push({ id: `${c.id}:notes_adulthood`, label: 'Adulthood', group });
    }
    return targets;
  }, []);

  // ── Navigation helpers ─────────────────────────────────────────────────────

  /** Navigate back to the History module after flushing pending writes. */
  const handleBackToHistory = useCallback(async () => {
    markRegenHandled();
    triggerSSIGeneration(currentReportSection);
    await flushStageWrites('semiStructuredInterview');
    navigate(`/patient/${patientId}/adult-autism/history`);
  }, [flushStageWrites, navigate, patientId, markRegenHandled, triggerSSIGeneration, currentReportSection]);

  /** Navigate to the review page after flushing all pending Firestore writes. */
  const handleGoToReview = useCallback(async () => {
    markRegenHandled();
    triggerSSIGeneration(currentReportSection);
    await flushStageWrites('semiStructuredInterview');
    navigate(`/patient/${patientId}/proforma/adult-asd/semi-structured-interview/review`);
  }, [flushStageWrites, navigate, patientId, markRegenHandled, triggerSSIGeneration, currentReportSection]);

  /** Navigate directly to the review page, bypassing validation. */
  const proceedToReview = useCallback(() => {
    setShowValidationDialog(false);
    handleGoToReview();
  }, [handleGoToReview]);

  /**
   * Navigate to a criterion or the Review page.
   * 'REVIEW' is a special sentinel — validates completion before navigating.
   *
   * Every panel maps to the single "Criteria" report section, so switching
   * panels never crosses a section boundary; we only trigger generation for the
   * section being left when there were edits.
   */
  const handleNavigate = useCallback((itemId) => {
    if (itemId === 'REVIEW') {
      // In preview mode skip the validation dialog — previewers are read-only
      // viewers and should not be blocked by incomplete evidence decisions.
      if (!isPreviewMode && incompleteSymptoms.length > 0) {
        setShowValidationDialog(true);
      } else {
        handleGoToReview();
      }
      return;
    }

    // All criteria (and the folded-in functional impairment content) belong to
    // the single "Criteria" report section, so navigating between panels never
    // crosses a section boundary. Trigger generation only when there are edits.
    if (hasChanges) {
      markRegenHandled();
      triggerSSIGeneration(currentReportSection);
      resetRegenHandled();
      setHasChanges(false);
    }

    setActiveItemId(itemId);
    const mainContentArea = document.querySelector('.flex-1.overflow-y-auto');
    if (mainContentArea?.scrollTo) {
      mainContentArea.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [isPreviewMode, incompleteSymptoms, handleGoToReview, currentReportSection, hasChanges, markRegenHandled, resetRegenHandled, triggerSSIGeneration]);

  const activeCriterion = SSI_CRITERIA.find(c => c.id === activeItemId);

  const activeLabel = activeCriterion
    ? `${activeCriterion.id}. ${activeCriterion.shortTitle}`
    : null;

  const breadcrumbTuples = [
    ['Patient Overview', `/patient/${patientId}/adult-autism`],
    ['Adult Autism Assessment'],
    ['Diagnostic Developmental History', `/patient/${patientId}/adult-autism/history`],
    ['Semi-Structured Interview'],
    ...(activeLabel ? [[activeLabel]] : []),
  ];

  const showChecklist = wf.isAppointmentChecklistEnabled;

  return (
    <Layout activePage="dashboard" scrollable={false}>
      <div className="flex flex-col h-full min-h-0">
      <div className="flex-shrink-0">
        <Breadcrumb
          tupleList={breadcrumbTuples}
          allowWrap={showChecklist}
          rightSlot={showChecklist
            ? <AppointmentChecklistControls appointmentType="developmentalHistory" />
            : undefined
          }
        />
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Left sidebar — shared ModularSideNavbar in compact mode. Each DSM-5
            criterion is a badge (its id, e.g. "A1"); a criterion is complete
            when both childhood and adulthood evidence are recorded, started when
            either is. Review is an extra row beneath a divider; module exit
            ("Back to History") lives in the fixed footer instead. */}
        <ModularSideNavbar
          mode={displayPreferences.sidebarMode}
          activeId={activeItemId}
          onSelect={handleNavigate}
          items={SSI_CRITERIA.map((c) => {
            const d = allData?.[c.id];
            const complete = !!(d?.evidence_childhood && d?.evidence_adulthood);
            const started = !!(d?.evidence_childhood || d?.evidence_adulthood);
            return {
              id: c.id,
              badge: c.id,
              label: c.shortTitle,
              fullTitle: c.fullTitle,
              title: c.shortTitle,
              status: complete ? 'completed' : started ? 'started' : 'default',
              isUnlocked: c.available !== false,
            };
          })}
          extraRows={[
            {
              id: 'REVIEW',
              label: 'Review',
              badge: <DocumentReportIcon className="w-3.5 h-3.5" />,
              active: activeItemId === 'REVIEW',
              onClick: () => handleNavigate('REVIEW'),
            },
          ]}
        />

        <div className="flex-1 min-w-0 flex flex-col min-h-0 overflow-hidden">
          {/* Preview mode info banner — read-only view for non-assigned clinicians */}
          {isPreviewMode && (
            <div className="px-8 py-2 bg-blue-50 border-b border-blue-100">
              <p className="text-sm text-blue-800">
                Preview mode — viewing only. Notes and decisions are read-only.
              </p>
            </div>
          )}
          {activeCriterion ? (
            <AdultAsdCriterionPanel
              criterion={activeCriterion}
              criterionData={allData[activeCriterion.id] || {}}
              onFieldChange={handleFieldChange}
              onMoveNote={handleMoveNoteAcross}
              moveTargets={noteMoveTargets}
              onNavigate={handleNavigate}
              onBackToHistory={handleBackToHistory}
              criteria={SSI_CRITERIA}
              displayPreferences={displayPreferences}
              readOnly={!canEdit}
              showDomains={wf?.isSSIDomainsImpactedEnabled !== false}
              seqFormData={seqFormData}
            />
          ) : (
            <div className="flex items-center justify-center h-96 text-gray-500 text-sm">
              Select a criterion from the sidebar
            </div>
          )}
        </div>
      </div>

      {/* Validation dialog — shown when Review is requested with incomplete decisions */}
      {showValidationDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowValidationDialog(false)}
          />
          <div className="relative bg-white rounded-xl shadow-2xl p-6 w-[560px] max-w-[90vw] text-left">
            <button
              onClick={() => setShowValidationDialog(false)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 text-xl leading-none"
              aria-label="Close"
            >
              &times;
            </button>
            <div className="flex items-center gap-2 mb-4">
              <ExclamationIcon className="w-6 h-6 text-amber-500 flex-shrink-0" />
              <h2 className="text-lg font-bold text-gray-900">
                Evidence Decisions Required
              </h2>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              The following DSM criteria are missing evidence decisions for one or
              both life stages.
            </p>
            <ul className="bg-red-50 rounded-lg p-3 pl-5 mb-4 list-disc list-inside space-y-1">
              {incompleteSymptoms.map((s) => (
                <li key={s.id} className="text-sm text-red-800 font-medium">
                  {s.id}. {s.shortTitle}
                </li>
              ))}
            </ul>
            <p className="text-sm italic text-amber-700 bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-5">
              <strong>Note:</strong> If you proceed without completing these, the
              criteria summary and auto-prefill on the Review page may be incomplete.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  const firstMissing = incompleteSymptoms[0];
                  if (firstMissing) {
                    setActiveItemId(firstMissing.id);
                    setShowValidationDialog(false);
                  }
                }}
                className="flex items-center py-2 px-4 rounded-md shadow-md bg-primary text-white hover:bg-primary/90 transition-all duration-200 font-semibold text-sm"
              >
                Go back to DSM Criteria
              </button>
              <button
                onClick={proceedToReview}
                className="flex items-center py-2 px-4 rounded-md shadow-md text-gray-900 border border-gray-300 hover:bg-gray-50 transition-all duration-200 font-semibold text-sm"
              >
                Proceed Anyway
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </Layout>
  );
}
